/**
 * Autore: Salvo de Luca
 * Azienda Dialogicom s.r.l.s.
 * File: src/components/auth/CustomForgotPassword.tsx
 * Release: v1.1.0 - Reset password custom endpoint
 * Data: 30/06/2025
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader, Mail, CheckCircle } from 'lucide-react';
import coronatekLogo from './asset/Coronatek_Logo_bordato_92dpi_200x88_1.webp';
import coronatekIntro from './asset/Intro_Coronatek.mp4';
import dialogicomLogo from './asset/Dialogicom_432x144.webp';

const CustomForgotPassword = () => {
  const { search } = useLocation();
  const skipVideo = search.includes('skipVideo=true');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(skipVideo);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!skipVideo) setShowForm(false);
  }, [skipVideo]);

  useEffect(() => {
    if (showForm) videoRef.current?.play();
  }, [showForm]);

  const handleVideoEnd = () => setShowForm(true);
  const handleVideoHover = () => videoRef.current?.play();

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage('Link di reset inviato! Controlla la tua email.');
      } else {
        setError(data.error || 'Errore durante l\'invio del link di reset');
      }
    } catch (err: any) {
      setError('Errore di connessione. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  if (!showForm) {
    return (
      <div className="fixed inset-0 z-50">
        <video
          ref={videoRef}
          src={coronatekIntro}
          autoPlay
          muted
          playsInline
          onEnded={handleVideoEnd}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[url('/src/components/auth/asset/bg-gradient.jpg')] bg-cover text-white overflow-auto">
      <div className="absolute top-0 left-0 right-0 flex justify-between items-center px-6 py-3 bg-slate-800 z-10 h-32">
        <img src={coronatekLogo} alt="Coronatek Logo" className="h-10 sm:h-16 drop-shadow-xl" />
        <div className="text-right">
          <h1 className="text-3xl sm:text-5xl font-bold text-green-400 mb-1 px-2">Turbomanager</h1>
          <p className="text-base sm:text-lg text-gray-400 px-2">Dalla presa in carico alla riconsegna</p>
        </div>
      </div>
      <div className="min-h-screen flex flex-col md:flex-row items-start gap-8 px-4 pt-40 pb-4">
        <div className="w-full md:w-1/2 animate-slide-in-left transition-all duration-1000 ease-out">
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 space-y-4">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-white flex items-center justify-center gap-2">
                <Mail className="h-5 w-5 text-blue-400" />
                Recupera Password
              </h2>
            </div>
            {successMessage && (
              <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-md text-sm flex items-center gap-2">
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
                <div>{successMessage}</div>
              </div>
            )}
            <form onSubmit={handleSendLink} className="space-y-3">
              <p className="text-gray-300 text-sm">
                Inserisci la tua email per ricevere un link di reset che ti permetterà di reimpostare la password.
              </p>
              <div className="bg-yellow-900/30 border border-yellow-600/50 text-yellow-200 px-4 py-3 rounded-md text-xs mb-4">
                <strong>Nota:</strong> Solo gli account verificati possono resettare la password. Se hai registrato l'account con Google, usa il pulsante "Continua con Google" nella pagina di login.
              </div>
              <input
                type="email"
                placeholder="Inserisci la tua email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded bg-slate-900 text-white border-slate-700 placeholder-gray-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/30 transition-all duration-200"
                autoComplete="email"
                required
              />
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white py-2 rounded transform transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin mr-2 h-5 w-5" />
                    Invio in corso...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-5 w-5" />
                    Invia link di reset
                  </>
                )}
              </button>
            </form>
            <div className="text-xs text-gray-400 text-center space-y-2">
              <div>
                <a href="/sign-in?skipVideo=true" className="hover:text-white transition-colors duration-200">
                  Ricordi la password? Accedi
                </a>
              </div>
              <div>
                <a href="/sign-up?skipVideo=true" className="hover:text-white transition-colors duration-200">
                  Non hai un account? Registrati
                </a>
              </div>
            </div>
            <div className="pt-2 text-center">
              <a
                href="https://dialogicom.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform duration-200 hover:scale-105"
              >
                <div className="text-xs text-gray-400 mb-1">Powered by</div>
                <img
                  src={dialogicomLogo}
                  alt="Dialogicom Logo"
                  className="mx-auto w-[200px]"
                />
              </a>
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/2 flex flex-col justify-between animate-slide-in-up transition-all duration-1000 ease-out">
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 w-full aspect-video overflow-hidden">
            <video
              ref={videoRef}
              src={coronatekIntro}
              muted
              autoPlay={skipVideo}
              onMouseOver={handleVideoHover}
              className="w-full h-full rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 w-full mt-2 animate-slide-in-up transition-all duration-1000 ease-out delay-500">
            <p className="text-gray-300 text-sm leading-relaxed mb-0">
              Non preoccuparti se hai dimenticato la password! Con <span className="font-semibold text-blue-400">TurboManager</span> puoi recuperare l'accesso in pochi semplici passaggi.
            </p>
            <p className="text-gray-300 text-sm leading-relaxed">
              La tua sicurezza è la nostra priorità: tutti i dati sono protetti e crittografati.
            </p>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slide-in-left {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slide-in-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-in-left { animation: slide-in-left 1s ease-out; }
        .animate-slide-in-up { animation: slide-in-up 1s ease-out; }
        .delay-500 { animation-delay: 0.5s; animation-fill-mode: both; }
      `}</style>
    </div>
  );
};

export default CustomForgotPassword;
